import { createSlice } from "@reduxjs/toolkit";

import { availabilityConfig } from "lib/thesaurus";
import { initialStateSelectors, TSelectors } from "../Thesaurus/reducer";

// Interface
type TOptions = {
  label: string;
  value: string | number;
};

interface IInitialState {
  data?: any;
  loading?: boolean;
  checkboxAll?: boolean;
  accessibility: {
    label: string;
    value: string;
  };
  dataOld?: any;

  goTo?: number;
  paged: boolean;
  currPage: number;
  pageSize?: number;

  optionsPx?: TOptions[];
  optionsAction?: TOptions[];

  px?: string;
  tAction?: TSelectors;

  input?: string;

  toggleFilter?: boolean;
}

type TUpdatedDataByKey = {
  data?: any;
  id?: string;
  key?: string;
  value?: string | number;
};

const initialState: IInitialState = {
  data: [],
  dataOld: [],
  loading: false,

  goTo: 1,
  paged: true,
  currPage: 0,
  pageSize: 20,

  input: "",
  checkboxAll: false,
  optionsPx: [],
  optionsAction: [],

  px: "4.5",
  tAction: initialStateSelectors,
  accessibility: initialStateSelectors,

  toggleFilter: false,
};

export const filteredDBData = (args: {
  action?: string,
  accessibility?: string,
  px?: number | string,
}) => (item: any) => {
  const { px, action, accessibility } = args;
  const isAction: boolean = !!action ? !!item?.action ? !!item?.action?.includes(action) : false : true;
  const isAccessibility : boolean = !!accessibility ? item?.onStock === availabilityConfig[accessibility] : true;
  const isPx: boolean = !!px ? !!item?.px ? +item?.px >= +px : true : true;

  return isPx && isAction && isAccessibility;
};

export const updatedDataByKey = (props: TUpdatedDataByKey) => {
  const { data = [], id = "", key = "", value, } = props;

  return data?.length ? data.map((item: any) => {
    if(item.id === id) {
      return {
        ...item,
        [key]: key === 'selected' ? !item.selected : value,
      }
    }
    return item;
  }) : [];
}

const filterData = (data: any, input: string) => {
  const searchInput = input.toLowerCase();
  return data.filter((item: any) => {
    return (
      (item?.id && item?.id?.toLowerCase().includes(searchInput)) ||
      (item?.cas && item?.cas?.toLowerCase().includes(searchInput)) ||
      (item?.name && item?.name.toLowerCase().includes(searchInput)) ||
      (item?.px && (item?.px+"").toLowerCase().includes(searchInput)) ||
      (item?.target && item?.target.toLowerCase().includes(searchInput)) ||
      (item?.action && item?.action.toLowerCase().includes(searchInput))
    );
  });
};

const mainFilterFunction = (data: any, input: string, args: {
  action?: string | number,
  accessibility?: string,
  px?: number | string,
}) => {
  return filterData(data, input).filter(filteredDBData({
    px: args.px || "",
    action: args.action as string,
    accessibility: args.accessibility as string
  }));
};

export const chemicalProbesSlice = createSlice({
  name: "chemicalProbes",
  initialState,
  reducers: {
    changeToggleFilter: (state) => {
      state.toggleFilter = !state.toggleFilter;
    },
    handlerShowAllData: (state) => {
      state.currPage = 0;
      state.checkboxAll = false;
      state.data = state.dataOld;
    },
    handlerSearch: (state) => {
      const input = state.input;
      const data = state.dataOld;

      state.currPage = 0;
      state.checkboxAll = false;

      const px = state?.px;
      const tAction = state?.tAction?.value;
      const accessibility = state?.accessibility?.value;

      state.data = mainFilterFunction(data, input || '', {
        px: px, action: tAction, accessibility: accessibility,
      });

      state.px = "4.5";
      state.tAction = initialStateSelectors;
      state.accessibility = initialStateSelectors;
    },
    handlerChangeInput: (state, action) => {
      state.input = action.payload;
    },

    handlerClearAllCheckboxSelected: (state) => {
      state.checkboxAll = false;

      state.data = state.data.map((item: any) => ({
        ...item,
          selected: false,
      }));
      state.dataOld = state.dataOld.map((item: any) => ({
        ...item,
        selected: false,
      }));
    },

    handlerChangePx: (state, action) => {
      const opt = action.payload;
      state.px = opt;

      // Data
      const data = state.dataOld;
      const input = state.input;

      state.currPage = 0;
      state.checkboxAll = false;

      // Filter params
      const px = opt;
      const tAction = state?.tAction?.value;
      const accessibility = state?.accessibility?.value;

      state.data = mainFilterFunction(data, input || '', {
        px: px,
        action: tAction,
        accessibility: accessibility,
      });
    },
    handlerChangeTarAction: (state, action) => {
      const opt = action.payload;
      state.tAction = opt;

      // Data
      const data = state.dataOld;

      state.currPage = 0;
      state.checkboxAll = false;
      const input = state.input;

      // Filter params
      const px = state.px;
      const tAction = opt?.value;
      const accessibility = state.accessibility?.value;

      state.data = mainFilterFunction(data, input || '', {
        px: px,
        action: tAction,
        accessibility: accessibility,
      });
    },
    handlerChangeAccessibility: (state, action) => {
      // Data
      const data = state.dataOld;
      const opt = action.payload;

      state.currPage = 0;
      state.checkboxAll = false;
      state.accessibility = opt;
      const input = state.input;

      // Filter params
      const px = state.px;
      const accessibility = opt?.value;
      const tAction = state.tAction?.value;

      state.data = mainFilterFunction(data, input || '', {
        px: px,
        action: tAction,
        accessibility: accessibility,
      });
    },

    handlerChangePriceItem: (state, action) => {
      const id = action.payload.id;
      const data = action.payload.data;

      state.data = updatedDataByKey({
        id: id,
        value: data,
        data: state.data,
        key: 'currentPrice',
      });
    },

    updatePricesAll: (state, action) => {
      state.data = action.payload;
    },
    handlerChangeGoTo: (state, action) => {
      state.goTo = action.payload;
    },

    getChemicalProbes: (state) => {
      state.loading = true;
    },
    getChemicalProbesError: (state) => {
      state.loading = false;
    },
    getChemicalProbesSuccess: (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
      state.dataOld = action.payload.data;
      state.optionsPx = action.payload.px;
      state.optionsAction = action.payload.action;
    },

    handlerCheckboxAll: (state) => {
      state.checkboxAll = !state.checkboxAll;

      state.data = state?.data?.map((item: any) => ({
        ...item,
        selected: !item.isPOA && item.onStock ? state.checkboxAll : false
      }));
    },
    changeCheckboxItem: (state, action) => {
      const id = action.payload.id;

      state.data = updatedDataByKey({
        id: id,
        value: "",
        key: 'selected',
        data: state.data,
      });
    },

    handlerChangeCurrentPriceType: (state, action) => {
      const id = action.payload.id;
      const type = action.payload.type;

      state.data = updatedDataByKey({
        id: id,
        value: type,
        data: state.data,
        key: 'currentTypePrice',
      });
    },

    handlerChangeQuantity: (state, action) => {
      const id = action.payload.id;
      const quantity = action.payload.quantity;

      state.data = updatedDataByKey({
        id: id,
        key: 'quantity',
        value: quantity,
        data: state.data,
      });
    },

    handlerChangeSize: (state, action) => {
      state.pageSize = action.payload;
    },
    handlerChangePage: (state, action) => {
      state.currPage = action.payload;
    },
  }
});

export const {
  changeToggleFilter,
  handlerShowAllData,

  handlerSearch,
  handlerChangeInput,

  handlerClearAllCheckboxSelected,

  handlerChangePx,
  handlerChangeTarAction,
  handlerChangeAccessibility,

  handlerChangePriceItem,
  updatePricesAll,

  handlerChangePage,
  handlerChangeGoTo,
  handlerChangeSize,

  getChemicalProbes,
  getChemicalProbesError,
  getChemicalProbesSuccess,

  handlerCheckboxAll,
  changeCheckboxItem,

  handlerChangeCurrentPriceType,

  handlerChangeQuantity
} = chemicalProbesSlice.actions;

export default chemicalProbesSlice.reducer;
